/**
 * Convert hex color to RGB format
 * @param {string} hex - Hex color (e.g., "#FF5733" or "FF5733")
 * @returns {string} - RGB format (e.g., "rgb(255, 87, 51)")
 */
export function hexToRgb(hex) {
  hex = hex.replace('#', '');
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  return `rgb(${r}, ${g}, ${b})`;
}

/**
 * Convert hex color to HSB format
 * @param {string} hex - Hex color (e.g., "#FF5733" or "FF5733")
 * @returns {string} - HSB format (e.g., "hsb(9, 80%, 100%)")
 */
export function hexToHsb(hex) {
  hex = hex.replace('#', '');
  const r = parseInt(hex.substr(0, 2), 16) / 255;
  const g = parseInt(hex.substr(2, 2), 16) / 255;
  const b = parseInt(hex.substr(4, 2), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const delta = max - min;

  let h = 0;
  if (delta !== 0) {
    if (max === r) {
      h = ((g - b) / delta) % 6;
    } else if (max === g) {
      h = (b - r) / delta + 2;
    } else {
      h = (r - g) / delta + 4;
    }
  }
  h = Math.round(h * 60);
  if (h < 0) h += 360;

  const s = max === 0 ? 0 : Math.round((delta / max) * 100);
  const bVal = Math.round(max * 100);

  return `hsb(${h}, ${s}%, ${bVal}%)`;
}

/**
 * Convert hex color to Pantone format (approximation)
 * Note: This is a simplified approximation. Real Pantone matching requires a color library.
 * @param {string} hex - Hex color (e.g., "#FF5733" or "FF5733")
 * @returns {string} - Pantone format (e.g., "PANTONE 17-1463 TPX")
 */
export function hexToPantone(hex) {
  // This is a simplified approximation
  // Real Pantone matching would require a comprehensive color library
  hex = hex.replace('#', '');
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);

  // Simple approximation based on RGB values
  // This is not accurate but provides a reasonable format
  const pantoneCode = Math.floor((r + g + b) / 3 / 2.55).toString().padStart(2, '0');
  const variant = Math.floor((r % 10) * 10 + (g % 10)).toString().padStart(2, '0');
  
  return `PANTONE ${pantoneCode}-${variant} C`;
}

/**
 * Format color based on selected format
 * @param {string} hex - Hex color (e.g., "#FF5733")
 * @param {string} format - Format type: 'hex', 'rgb', 'hsb', 'pantone'
 * @returns {string} - Formatted color string
 */
export function formatColor(hex, format) {
  if (!hex) return '';
  
  switch (format) {
    case 'rgb':
      return hexToRgb(hex);
    case 'hsb':
      return hexToHsb(hex);
    case 'pantone':
      return hexToPantone(hex);
    case 'hex':
    default:
      return hex.toUpperCase();
  }
}

